import { z } from "zod";
import {
  CHECKIN_CLOSE_REASONS,
  closeReasonCodeSchema,
  workQueueItemSchema,
} from "./work-queue";

// TES-899 — the coach's handling of one weekly check-in (F-04).
//
// Emelie 2026-09-23: the same reply could be sent twice because sending
// created a chat message and left the check-in looking unanswered. The
// check-in now carries an explicit state, and every surface that answers it
// goes through ONE action ("Skicka svar och slutför") that is idempotent on
// the check-in: a repeat returns the first result instead of a second
// message. "Avsluta utan svar" is its own action and needs a reason.

export const CHECKIN_COACH_STATES = [
  "open",
  "answered",
  "closed_without_reply",
] as const;
export const checkinCoachStateSchema = z.enum(CHECKIN_COACH_STATES);
export type CheckinCoachState = z.infer<typeof checkinCoachStateSchema>;

/** "Skicka svar och slutför". */
export const sendCheckinCoachReplySchema = z.object({
  // Same bounds as an ordinary chat message: the reply IS one.
  body: z.string().trim().min(1).max(4000),
  // The AI draft the text started from, when there was one. Recorded as used
  // in the same transaction, instead of a second best-effort call.
  aiDraftId: z.string().uuid().optional(),
});
export type SendCheckinCoachReplyRequest = z.infer<
  typeof sendCheckinCoachReplySchema
>;

/**
 * "Avsluta utan svar" — the reason is mandatory. TES-1208: it is one of
 * A-01's check-in reasons (CHECKIN_CLOSE_REASONS), with an optional note that
 * "Annat" requires ("annat med obligatorisk fritext").
 */
export const closeCheckinWithoutReplySchema = z
  .object({
    reasonCode: closeReasonCodeSchema.refine(
      (code) => CHECKIN_CLOSE_REASONS.some((o) => o.code === code),
      { message: "reason_not_for_checkin" },
    ),
    reason: z.string().trim().max(2000).optional(),
  })
  .superRefine((value, ctx) => {
    if (value.reasonCode === "other" && !value.reason) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["reason"],
        message: "note_required_for_other",
      });
    }
  });
export type CloseCheckinWithoutReplyRequest = z.infer<
  typeof closeCheckinWithoutReplySchema
>;

export const checkinCoachHandlingSchema = z.object({
  submissionId: z.string(),
  state: checkinCoachStateSchema,
  // Set once the check-in is handled; null while open.
  handledAt: z.string().nullable(),
  handledByEmployeeId: z.string().nullable(),
  // The chat message the reply produced (answered only).
  chatMessageId: z.string().nullable(),
});
export type CheckinCoachHandling = z.infer<typeof checkinCoachHandlingSchema>;

/**
 * The check-in's own record of how it was handled, for the coach's patient
 * page: the reply she sent (read from the coach thread) or why she closed it
 * without one. Coach only — the reply is coach-chat content.
 */
export const checkinCoachHandlingDetailSchema = z.object({
  submissionId: z.string(),
  state: checkinCoachStateSchema,
  handledAt: z.string().nullable(),
  // answered: the linked coach-thread message as the chat shows it now (an
  // edit shows, a deletion shows the placeholder). Null when open, closed,
  // or when the message no longer exists.
  reply: z
    .object({
      body: z.string(),
      createdAt: z.string(),
      editedAt: z.string().nullable(),
      deletedAt: z.string().nullable(),
    })
    .nullable(),
  // closed_without_reply only: the coach's note (required for "Annat",
  // optional otherwise; the whole text of a reason given before TES-1208).
  reason: z.string().nullable(),
  // TES-1208 — closed_without_reply only. A check-in closed before the codes
  // existed reads `other`. Optional so an older API response still parses.
  reasonCode: closeReasonCodeSchema.nullable().optional(),
});
export type CheckinCoachHandlingDetail = z.infer<
  typeof checkinCoachHandlingDetailSchema
>;

export const checkinCoachActionResultSchema = checkinCoachHandlingSchema.extend(
  {
    // True when this call found the check-in already handled the same way
    // and returned that result rather than acting again.
    replayed: z.boolean(),
    // The next item in the caller's work queue, so the card loop continues.
    next: workQueueItemSchema.nullable(),
  },
);
export type CheckinCoachActionResult = z.infer<
  typeof checkinCoachActionResultSchema
>;
